import { TypeAccepted } from '@/components/Upload';
import { cancelUpload, uploadFile } from '@/services/S3Service';
import { removeSpacesAndSpecialCharacters } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { lookup } from 'mime-types';
import React, { MutableRefObject, SyntheticEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'underscore';
import { useTypesAcceptedUpload } from './FileTypesAcceptedUpload';

export interface PreviewFile {
    uri?: string;
    name?: string;
}

export interface FileUploadProps {
    typeAccepted?: TypeAccepted;
    isLogo?: boolean;
    isBanner?: boolean;
    shortLabel?: boolean;
    previewFile?: PreviewFile | undefined;
    fileNameLimitSize?: number;
    inputRef?: MutableRefObject<HTMLInputElement | null>;
    customFolder?: string;
    isFullImageTypeAccepted?: boolean;
}

export interface UploadProcess {
    progress: number;
    uploading: boolean;
}

export interface FileUploadData {
    key: string;
    file: File | null;
    fileName: string;
    preview: string;
    local: string;
}

const initialFileUploadDataState: FileUploadData = { key: '', file: null, fileName: '', preview: '', local: '' };
const initialUploadProcessState: UploadProcess = { progress: 0, uploading: false };

export const useFileUpload = ({
    typeAccepted = 'IMAGE',
    isLogo = false,
    isBanner = false,
    shortLabel = false,
    previewFile = undefined,
    fileNameLimitSize = 255,
    inputRef = undefined,
    customFolder = undefined,
    isFullImageTypeAccepted = false,
}: FileUploadProps) => {
    const { strings } = useSelector((state: ApplicationState) => ({ strings: state.locale.data }));

    const [uploadProcess, setUploadProcess] = useState<UploadProcess>(initialUploadProcessState);
    const [fileUploadData, setFileUploadData] = useState<FileUploadData>(initialFileUploadDataState);

    const { acceptConfiguration } = useTypesAcceptedUpload(typeAccepted, isLogo, isBanner, shortLabel, customFolder, isFullImageTypeAccepted);
    const { folder, accept } = acceptConfiguration;

    const getNormalizedFileName = (fileName: string): string => {
        const fileExt = `.${_.last(fileName.split('.'))}`;
        const name = removeSpacesAndSpecialCharacters(fileName)
            .replace(fileExt, '')
            .substring(0, fileNameLimitSize - fileExt.length);

        return `${name}${fileExt}`;
    };

    const processFile = async (file: File, key: string, onUploadStartHandler?: CallableFunction, onUploadCompleteHandler?: CallableFunction) => {
        const isValidFileType = !accept || !!accept.split(',').find((type: string) => type === lookup(file.name));

        if (isValidFileType) {
            onUploadStartHandler && onUploadStartHandler();

            const img = URL.createObjectURL(file);
            const url = await uploadFile(file, key, (progress: number) => setUploadProcess({ uploading: true, progress }));
            const uploadedFile: FileUploadData = { file, fileName: getNormalizedFileName(file.name), key, preview: img, local: img };

            setFileUploadData(uploadedFile);
            setUploadProcess(initialUploadProcessState);

            if (url !== null && onUploadCompleteHandler) {
                onUploadCompleteHandler(uploadedFile);
            }
        } else {
            if (inputRef && inputRef.current) {
                inputRef.current.value = '';
            }
            toast.error(strings.toastMessages.FILE_TYPE_NOT_ALLOWED);
        }
    };

    const selectFileHandler = async (
        event: React.ChangeEvent<HTMLInputElement>,
        onUploadStartHandler?: CallableFunction,
        onUploadCompleteHandler?: CallableFunction,
    ) => {
        setFileUploadData((fileUploadDataState: FileUploadData) => ({ ...fileUploadDataState, preview: '' }));

        if (event.target.files && event.target.files.length === 1) {
            const file = event.target.files[0];
            processFileHandler(file, onUploadStartHandler, onUploadCompleteHandler);
        }
    };

    const processFileHandler = (file: File, onUploadStartHandler?: CallableFunction, onUploadCompleteHandler?: CallableFunction) => {
        const key = `${folder}${new Date().getTime()}-${getNormalizedFileName(file.name)}`;
        setFileUploadData((fileUploadDataState: FileUploadData) => ({ ...fileUploadDataState, key }));
        processFile(file, key, onUploadStartHandler, onUploadCompleteHandler);
    };

    const cancelHandler = (event: SyntheticEvent, onUploadCancelHandler?: CallableFunction, onRemoveHandler?: CallableFunction) => {
        event.stopPropagation();
        event.preventDefault();

        onUploadCancelHandler && onUploadCancelHandler();

        if (fileUploadData.key) {
            cancelUpload(fileUploadData.key);
            setUploadProcess(initialUploadProcessState);
            setFileUploadData(initialFileUploadDataState);
            onRemoveHandler && onRemoveHandler();
        }
    };

    const removeHandler = (onRemoveFileLinkClickHandler?: CallableFunction, onRemoveHandler?: CallableFunction) => {
        if (onRemoveFileLinkClickHandler) {
            onRemoveFileLinkClickHandler();
        }
        setFileUploadData(initialFileUploadDataState);
        onRemoveHandler && onRemoveHandler();
    };

    useEffect(() => {
        if (previewFile?.uri && previewFile?.name) {
            const fileName = _.last(previewFile.name?.split('-')) || '';

            setFileUploadData((fileUploadData: FileUploadData) => ({
                ...fileUploadData,
                fileName,
                preview: previewFile.uri || '',
            }));
        }
    }, [previewFile]);

    return {
        acceptConfiguration,
        fileUploadData,
        uploadProcess,
        selectFileHandler,
        processFileHandler,
        cancelHandler,
        removeHandler,
        setFileUploadData,
    };
};
